import PageLayout from "Layouts/PageLayout";
import {useTranslation} from "react-i18next";
import CalculatorIcon from "../../Assets/SVG/CalculatorIcon";
import Separator from "Components/Separator";
import List, {IColumnProps} from "../../Components/List/List";
import React, {useMemo, useRef, useState} from "react";
import {formatNumber} from "../../Utils/NumberUtils";
import {SalesItemsDto, ShopifyOrderDto} from "../../Apis/Models/SettlementSalesDto";
import Label from "../../Components/Label";
import Select from "../../Components/Select";
import {IQueryParam} from "../../Apis/Models/IQueryParams";
import useFilteredList from "../../Hooks/UseFilteredList";
import useQueryParams from "../../Hooks/UseQueryParams";
import DatePicker from "../../Components/DatePicker";
import moment from "moment";
import {Bar, BarChart, Tooltip, XAxis, YAxis} from "recharts";
import InventoryIcon from "../../Assets/SVG/InventoryIcon";
import MoneyIcon from "../../Assets/SVG/MoneyIcon";
import {TotalComponentV2} from "../../Components/TotalComponent";
import {join} from "lodash";
import {
    useImportShopifyOrder,
    useImportUnknownShopifyOrders
} from "../../Apis/ShopifyOrderApi";
import FlexRow from "../../Components/FlexRow";
import OrdersIcon from "../../Assets/SVG/OrdersIcon";
import SideModal from "../../Components/SideModal";
import FormFieldBox from "../../Components/FormFieldBox";
import Space from "../../Components/Space";
import DotsIcon from "../../Assets/SVG/DotsIcon";
import {useSettlementProjects, useSettlementSales} from "../../Apis/ProjectSettlementApi";
import {canAccess} from "../../Utils/CanAccess";
import {useUserContext} from "../../Providers/UserContextProvider";
import Button from "../../Components/Button";

const SettlementSales = () => {

    const {t} = useTranslation();

    const [filter, setFilter] = useQueryParams();
    const {user} = useUserContext();

    const chartsDivRef = useRef<HTMLDivElement>(null)

    const isAdmin = canAccess(user.permissions, 'Admin')

    const {data: projects} = useSettlementProjects();
    const {data: settlementSales, isLoading} = useSettlementSales(filter);
    const [selectedOrder, setSelectedOrder] = useState<ShopifyOrderDto | undefined>(undefined);
    const {data: orderSales} = useSettlementSales({
        ...filter,
        project: undefined,
        orderTitle: selectedOrder?.orderTitle
    });

    const {mutateAsync: importUnknownOrders, isLoading: isReimportingUnknown} = useImportUnknownShopifyOrders();

    const {mutateAsync: importShopifyOrder} = useImportShopifyOrder();

    const onlyUnknowns = useMemo(() => [
        {value: 'false', label: t('no')},
        {value: 'true', label: t('yes')},
    ], [t]);

    const includeUnfulfilled = useMemo(() => [
        {value: 'false', label: t('no')},
        {value: 'true', label: t('yes')},
    ], [t]);

    const [filteredShowTypes, setShowTypeFilter] = useFilteredList([{
        key: 'orders',
        name: t('orders')
    }, {key: 'orderLines', name: t('OrderLines')}], x => [x.name]);
    const [filteredProjects, setProjectFilter, projectFilterValue] = useFilteredList(projects ?? [], x => [x.name]);
    const [filteredUnknowns, setFilteredUnknowns] = useFilteredList(onlyUnknowns, x => [x.label]);
    const [filteredUnfulfilled, setFilteredUnfulfilled] = useFilteredList(includeUnfulfilled, x => [x.label]);

    const setQueryField = (fieldName: 'from' | 'to' | 'projectId' | 'onlyUnknown' | 'showType' | 'includeUnfulfilled', value: IQueryParam) => {
        const newValue = {...filter, [fieldName]: value}
        setFilter(newValue, true);
    }

    const orderLineColumns = [
        {
            title: t('productSku'),
            textRender: (record: SalesItemsDto) => record.productSku,
        },
        {
            title: t('productName'),
            textRender: (record: SalesItemsDto) => record.productName,
        },
        {
            title: t('variantName'),
            textRender: (record: SalesItemsDto) => record.variantName,
        },
        {
            title: t('totalSales'),
            textRender: (record: SalesItemsDto) => {
                let strings = [];

                strings.push(record.quantity?.toString());
                if (record.b2BCount > 0) {
                    strings.push(`(B2B: ${record.b2BCount})`);
                }
                if (record.refundedQuantity > 0) {
                    strings.push(`(${t('refunded')}: ${record.refundedQuantity})`);
                }

                return join(strings, ' ');
            },
        },
        {
            title: t('refunds'),
            textRender: (record: SalesItemsDto) => record.refundedQuantity?.toString() ?? '0',
        },
        {
            title: t('totalDiscounts'),
            textRender: (record: SalesItemsDto) => formatNumber(record.totalDiscounts),
        },
        {
            title: t('totalRefunds'),
            textRender: (record: SalesItemsDto) => formatNumber(record.totalRefunds),
        },
        {
            title: t('totalCost'),
            textRender: (record: SalesItemsDto) => formatNumber(record.totalCosts),
        },
        {
            title: t('totalSalesExclTax'),
            textRender: (record: SalesItemsDto) => formatNumber(record.totalSalesPriceExclVat),
        },
        {
            title: t('totalTax'),
            textRender: (record: SalesItemsDto) => formatNumber(record.totalVat),
        },
        {
            title: t('totalSalesInclTax'),
            textRender: (record: SalesItemsDto) => formatNumber(record.totalSalesPriceInclVat),
        },
        {
            title: t('totalSalesInclTaxAfterRefunds'),
            textRender: (record: SalesItemsDto) => formatNumber(record.totalSalesAfterRefunds),
        },
    ]

    const orderColumns: IColumnProps<any>[] = [
        {
            title: t('orderTitle'),
            textRender: (record: ShopifyOrderDto) => {
                let text = record.orderTitle;

                if (record.waitingForImport) {
                    return `${text} (${t('waitingForImport')})`;
                }

                return text
            },
        },
        {
            title: t('placedAt'),
            textRender: (record: ShopifyOrderDto) => moment(record.placedAt).format('DD.MM.YYYY HH:mm'),
        },
        {
            title: t('fulfilledAt'),
            textRender: (record: ShopifyOrderDto) => record.fulfilledAt ? moment(record.fulfilledAt).format('DD.MM.YYYY HH:mm') : t(''),
        },
        {
            title: t('total'),
            textRender: (record: ShopifyOrderDto) => formatNumber(record.total),
        }
    ]

    if (isAdmin) {
        orderColumns.push({
            title: t('functions'),
            customRender: (record: ShopifyOrderDto) => (
                <FlexRow justify={"center"} align={"center"}>
                    <FlexRow title={t('reimport')} className={"clickable"} justify={"center"} align={"center"}
                             onClick={() => importShopifyOrder(record)}>
                        <CalculatorIcon/>
                    </FlexRow>
                    <FlexRow title={t('reimport')} className={"clickable"} justify={"center"} align={"center"}
                             onClick={() => setSelectedOrder(record)}>
                        <DotsIcon/>
                    </FlexRow>
                </FlexRow>
            )
        })
    }

    return (
        <>
            <PageLayout title={t('settlements')} icon={<CalculatorIcon/>} header={(
                <div>
                    {(isAdmin && filter['onlyUnknown'] === 'true' && (!!settlementSales?.salesItems && settlementSales.salesItems.length > 0)) && (
                        <>
                            <div>
                                <Button prefix={<MoneyIcon fill={"#FFF"}/>}
                                        text={t('reimportUnknown')} loading={isReimportingUnknown}
                                        type={"secondary"}
                                        onClick={importUnknownOrders}/>
                            </div>
                        </>
                    )}
                </div>
            )}>
                <div>

                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20}}>

                        <div>
                            <Label placeholder={t('from')}/>
                            <DatePicker name={'from'}
                                        onChange={value => setQueryField('from', value ? moment(value).format('YYYY-MM-DD') : '')}
                                        value={!!filter['from'] ? filter['from'].toString() : undefined}/>
                        </div>

                        <div>
                            <Label placeholder={t('to')}/>
                            <DatePicker name={'to'}
                                        onChange={value => setQueryField('to', value ? moment(value).format('YYYY-MM-DD') : '')}
                                        value={!!filter['to'] ? filter['to'].toString() : undefined}/>
                        </div>

                        <div>
                            <Label placeholder={t('showType')}/>
                            <Select name={"showType"} value={filter['showType']}
                                    onChange={value => setQueryField('showType', value)} onFilter={setShowTypeFilter}>
                                {filteredShowTypes?.map(showType => <Select.Option key={showType.key}
                                                                                   value={showType.key}
                                                                                   label={showType.name}/>)}
                            </Select>
                        </div>

                        <div>
                            <Label placeholder={t('project')}/>
                            <Select name={"projectId"} value={filter['projectId']}
                                    onChange={value => setQueryField('projectId', value)} onFilter={setProjectFilter}>
                                {!projectFilterValue && (
                                    <Select.Option value={undefined} label={isAdmin ? t('all') : t('select')}/>
                                )}
                                {filteredProjects?.map(project => <Select.Option key={project.id} value={project.id}
                                                                                 label={project.name}/>)}
                            </Select>
                        </div>

                        <div>
                            <Label placeholder={t('showUnfulfilled')}/>
                            <Select name={"includeUnfulfilled"} value={filter['includeUnfulfilled']}
                                    onChange={value => setQueryField('includeUnfulfilled', value)}
                                    onFilter={setFilteredUnfulfilled}>
                                {filteredUnfulfilled?.map(project => <Select.Option key={project.value}
                                                                                    value={project.value}
                                                                                    label={project.label}/>)}
                            </Select>
                        </div>


                        {isAdmin && (
                            <>
                                {filter['showType'] === 'orderLines' && (
                                    <>
                                        <div>
                                            <Label placeholder={t('onlyUnknown')}/>
                                            <Select name={"onlyUnknown"} value={filter['onlyUnknown']}
                                                    onChange={value => setQueryField('onlyUnknown', value)}
                                                    onFilter={setFilteredUnknowns}>
                                                {filteredUnknowns?.map(project => <Select.Option key={project.value}
                                                                                                 value={project.value}
                                                                                                 label={project.label}/>)}
                                            </Select>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    <Separator style={{margin: '30px 0'}}/>
                    <div>
                        {t('allPricesAreExclVatMessage')}
                    </div>
                    <Separator style={{margin: '30px 0'}}/>

                    <div ref={chartsDivRef}>
                        <BarChart width={chartsDivRef.current?.clientWidth} height={200}
                                  data={settlementSales?.salesByDate ?? []}>
                            <YAxis tickCount={5} axisLine={false} tickLine={false}/>
                            <XAxis axisLine={false} tickLine={false} dataKey="date" style={{fontWeight: '700'}}/>
                            <Bar dataKey="count" fill="#E91D25" style={{borderRadius: '5px 5px 0 0'}}
                                 name={t('numberOfSales').toString()}/>
                            <Bar dataKey="itemCount" fill="#707070" style={{borderRadius: '5px 5px 0 0'}}
                                 name={t('numberOfItemsSold').toString()}/>
                            <Tooltip/>
                        </BarChart>
                    </div>

                    <Separator style={{margin: '30px 0'}}/>

                    {filter['showType'] === 'orders' ? (
                        <>
                            <List<ShopifyOrderDto> data={settlementSales?.orders} loading={isLoading}
                                                   columns={orderColumns} keyExtractor={(record) => record.orderTitle}/>
                            <TotalComponentV2
                                items={[
                                    {
                                        icon: <OrdersIcon/>,
                                        header: t('orderQuantity'),
                                        total: settlementSales?.orders.length.toString() ?? ''
                                    },
                                    {
                                        icon: <InventoryIcon/>,
                                        header: t('itemQuantity'),
                                        total: settlementSales?.salesItems.reduce((acc, curr) => acc + curr.quantity, 0).toString() ?? ''
                                    },
                                    {
                                        icon: <MoneyIcon/>,
                                        header: t('total'),
                                        total: `${formatNumber(settlementSales?.orders.reduce((acc, curr) => acc + curr.total, 0) ?? 0)} DKK`
                                    },
                                ]}/>
                        </>
                    ) : (
                        <>
                            <List<SalesItemsDto> data={settlementSales?.salesItems} loading={isLoading}
                                                 columns={orderLineColumns}
                                                 keyExtractor={(record) => record.productSku?.toString()}/>
                            <TotalComponentV2
                                items={[
                                    {
                                        icon: <InventoryIcon/>,
                                        header: t('quantity'),
                                        total: settlementSales?.salesItems.reduce((acc, curr) => acc + curr.quantity, 0).toString() ?? ''
                                    },
                                    {
                                        icon: <MoneyIcon/>,
                                        header: t('totalCost'),
                                        total: `${formatNumber(settlementSales?.salesItems.reduce((acc, curr) => acc + curr.totalCosts, 0) ?? 0)} DKK`
                                    },
                                    {
                                        icon: <MoneyIcon/>,
                                        header: t('total'),
                                        total: `${formatNumber(settlementSales?.salesItems.reduce((acc, curr) => acc + curr.totalSalesPriceExclVat, 0) ?? 0)} DKK`
                                    },
                                    {
                                        icon: <MoneyIcon/>,
                                        header: t('totalInclVat'),
                                        total: `${formatNumber(settlementSales?.salesItems.reduce((acc, curr) => acc + curr.totalSalesPriceInclVat, 0) ?? 0)} DKK`
                                    },
                                ]}/>
                        </>
                    )}
                </div>
            </PageLayout>

            <SideModal closeOnOutsideClick visible={!!selectedOrder} title={selectedOrder?.orderTitle ?? ''}
                       onCancel={() => setSelectedOrder(undefined)}>
                <Space>
                    {orderSales?.salesItems.map(item => (
                        <FormFieldBox>
                            <FlexRow direction={"row"} align={"start"}>
                                <img alt={""} src={item.imageUrl}
                                     style={{width: 'auto', height: '70px', paddingRight: 10}}/>
                                <FlexRow direction={"column"} align={"start"} style={{gap: 5}}>
                                    <div>{item.productSku}</div>
                                    <div style={{fontWeight: 'bold'}}>
                                        {item.productName} x {item.quantity}
                                    </div>
                                    <div>{item.variantName}</div>
                                </FlexRow>
                                <div className={'no-wrap flex align-center justify-center'}>
                                    {formatNumber(item.totalSalesPriceExclVat)} DKK
                                </div>
                            </FlexRow>
                        </FormFieldBox>
                    ))}
                    <TotalComponentV2 items={[
                        {
                            icon: <InventoryIcon/>,
                            header: t('quantity'),
                            total: orderSales?.salesItems.reduce((acc, curr) => acc + curr.quantity, 0).toString() ?? ''
                        },
                        {
                            icon: <MoneyIcon/>,
                            header: t('total'),
                            total: `${formatNumber(orderSales?.orders.reduce((acc, curr) => acc + curr.total, 0) ?? 0)}`
                        },
                    ]}/>
                </Space>
            </SideModal>
        </>
    )
}

export default SettlementSales
